/**
 * Liquid Addition Form Component
 * Draggable overlay window for recording liquid poured into test diaper
 */

import React, { useState, useRef, useEffect } from 'react';
import { addLiquidAddition } from '../../lib/api-clip-monitor';

interface LiquidAdditionFormProps {
  clipAddress: string;
  isOpen: boolean;
  onClose: () => void;
  onAddition?: () => void; // Callback after successful addition
  diaperCounter?: number | null; // Current diaper counter for grouping
}

const CHANNEL_LOCATIONS = ['C1', 'C2', 'C3', 'C4', 'C5'];

export const LiquidAdditionForm: React.FC<LiquidAdditionFormProps> = ({
  clipAddress,
  isOpen,
  onClose,
  onAddition,
  diaperCounter,
}) => {
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const windowRef = useRef<HTMLDivElement>(null);
  const [volume, setVolume] = useState('200');
  const [duration, setDuration] = useState('25');
  const [location, setLocation] = useState('C3');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (windowRef.current) {
      const rect = windowRef.current.getBoundingClientRect();
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const volumeNum = parseFloat(volume);
    const durationNum = parseFloat(duration);

    if (isNaN(volumeNum) || volumeNum <= 0) {
      setError('Volume must be a positive number');
      return;
    }

    if (isNaN(durationNum) || durationNum <= 0) {
      setError('Duration must be a positive number');
      return;
    }

    setIsSubmitting(true);

    try {
      await addLiquidAddition(
        clipAddress, 
        volumeNum, 
        durationNum, 
        location, 
        diaperCounter !== null ? diaperCounter : undefined
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      // Call callback to refresh data
      if (onAddition) {
        onAddition();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record liquid addition');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Draggable Window */}
      <div
        ref={windowRef}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          zIndex: 50,
        }}
        className="bg-white border-2 border-blue-500 rounded-lg shadow-2xl"
      >
        {/* Title Bar - Draggable */}
        <div
          onMouseDown={handleMouseDown}
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-t-md cursor-move flex items-center justify-between select-none"
        >
          <div className="flex items-center gap-2">
            <span>💧</span>
            <div>
              <div className="font-semibold">Simulate Liquid Addition</div>
              {diaperCounter !== null && diaperCounter !== undefined && (
                <div className="text-xs text-blue-100">Diaper #{diaperCounter}</div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-700 rounded px-2 py-0.5 text-lg leading-none"
            type="button"
          >
            ×
          </button>
        </div>

        {/* Form Content */}
        <div className="p-4 w-80">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label htmlFor="volume" className="block text-sm font-medium text-gray-700 mb-1">
                Volume (mL)
              </label>
              <input
                type="number"
                id="volume"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                min="1"
                max="2000"
                step="1"
                required
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                Duration (seconds)
              </label>
              <input
                type="number"
                id="duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                min="1"
                max="300"
                step="1"
                required
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                Location (Carbon Lane)
              </label>
              <select
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                required
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {CHANNEL_LOCATIONS.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}

            {success && (
              <div className="p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                ✓ Liquid addition recorded successfully!
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isSubmitting ? 'Adding...' : 'Add Liquid'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};
